import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  supabase,
  isSupabaseConfigured,
  classifySupabaseError,
  reportSchemaError,
} from 'lib/supabase';
import { deriveNameFromEmail } from 'lib/currentUser';

// ---------------------------------------------------------------------------
// UserProfilesContext
//
// One entry per authenticated teammate — the source of truth for "who is
// this uuid" wherever the app needs to attribute a piece of data to the
// person who logged it. Read by the Collaboration Hub roster, the
// activity feed, the goal-detail progress entries, and the incident
// timeline.
//
// Live mode: Supabase's public.user_profiles table (see migration
//            0009_user_profiles.sql). Subscribed to postgres_changes so
//            "Jane just updated her role to SLP" shows up on Michael's
//            screen without a refresh.
//
// Demo mode: A single stub profile derived from the localStorage
//            userSession. The rest of the roster gets a small
//            demonstration list so the Collaboration Hub page still
//            renders convincingly for sales walkthroughs.
// ---------------------------------------------------------------------------

const TABLE = 'user_profiles';
const DEMO_STORAGE_KEY = 'edutracker.user_profiles.v1';
const SESSION_KEY = 'userSession';

const UserProfilesContext = createContext(null);

// ---------------------------------------------------------------------------
// Role + discipline vocabulary. Central so the Collaboration Hub form and
// the attribution rendering agree on label spelling and color-coding.
// ---------------------------------------------------------------------------

export const ROLE_OPTIONS = [
  { value: 'teacher',          label: 'Special Education Teacher' },
  { value: 'admin',            label: 'Administrator' },
  { value: 'paraprofessional', label: 'Paraprofessional' },
  { value: 'related_service',  label: 'Related Service Provider' },
  { value: 'case_manager',     label: 'Case Manager' },
  { value: 'other',            label: 'Other' },
];

// Recognized specialties within the "related service" role, plus a couple
// of common cross-role specialties. The dropdown accepts a custom string
// too — see the Collaboration Hub form.
export const DISCIPLINE_OPTIONS = [
  { value: 'SLP',                 label: 'Speech-Language Pathologist' },
  { value: 'OT',                  label: 'Occupational Therapist' },
  { value: 'PT',                  label: 'Physical Therapist' },
  { value: 'Behavior Specialist', label: 'Behavior Specialist' },
  { value: 'BCBA',                label: 'BCBA' },
  { value: 'School Psychologist', label: 'School Psychologist' },
  { value: 'Counselor',           label: 'Counselor' },
  { value: 'Social Worker',       label: 'Social Worker' },
  { value: 'Nurse',               label: 'School Nurse' },
];

export function roleLabelFor(role) {
  const hit = ROLE_OPTIONS.find((r) => r.value === role);
  return hit ? hit.label : (role || '');
}

// Short badge label — "SLP", "OT", "Teacher", "Admin" — for tight spots
// like activity feed rows and progress-entry attribution.
export function shortRoleLabel(profile) {
  if (!profile) return '';
  if (profile.discipline) return profile.discipline;
  switch (profile.role) {
    case 'teacher':          return 'Teacher';
    case 'admin':            return 'Admin';
    case 'paraprofessional': return 'Para';
    case 'related_service':  return 'Related Service';
    case 'case_manager':     return 'Case Mgr';
    case 'other':            return 'Team member';
    default:                 return '';
  }
}

// Pick a background/foreground pair for the little role pill in the UI.
// Tailwind classes only, so this can be interpolated with template
// literals without a runtime CSS pipeline.
export function roleColorClasses(profile) {
  const key = (profile?.discipline || profile?.role || '').toLowerCase();
  if (key.includes('slp'))         return 'bg-purple-100 text-purple-800';
  if (key.includes('ot') || key === 'occupational therapist')
                                    return 'bg-emerald-100 text-emerald-800';
  if (key.includes('pt') || key === 'physical therapist')
                                    return 'bg-teal-100 text-teal-800';
  if (key.includes('behavior'))    return 'bg-orange-100 text-orange-800';
  if (key.includes('psycholog'))   return 'bg-indigo-100 text-indigo-800';
  if (key.includes('counselor'))   return 'bg-sky-100 text-sky-800';
  if (key.includes('social'))      return 'bg-rose-100 text-rose-800';
  if (key.includes('nurse'))       return 'bg-red-100 text-red-800';
  switch (profile?.role) {
    case 'teacher':          return 'bg-blue-100 text-blue-800';
    case 'admin':            return 'bg-slate-100 text-slate-800';
    case 'paraprofessional': return 'bg-amber-100 text-amber-800';
    case 'case_manager':     return 'bg-cyan-100 text-cyan-800';
    default:                 return 'bg-muted text-muted-foreground';
  }
}

// ---------------------------------------------------------------------------
// snake_case → camelCase mapping
// ---------------------------------------------------------------------------

function normalizeIncoming(row) {
  if (!row) return null;
  return {
    id:           row.id,
    email:        row.email ?? '',
    displayName:  row.display_name ?? row.displayName ?? '',
    role:         row.role ?? '',
    discipline:   row.discipline ?? '',
    photoUrl:     row.photo_url ?? row.photoUrl ?? '',
    createdAt:    row.created_at ?? row.createdAt ?? null,
    lastUpdated:  row.last_updated ?? row.lastUpdated ?? null,
  };
}

function toPatchRow(patch) {
  const row = {};
  if (!patch || typeof patch !== 'object') return row;
  if ('displayName' in patch) row.display_name = patch.displayName || null;
  if ('role'        in patch) row.role         = patch.role || null;
  if ('discipline'  in patch) row.discipline   = patch.discipline || null;
  if ('photoUrl'    in patch) row.photo_url    = patch.photoUrl || null;
  return row;
}

// ---------------------------------------------------------------------------
// Local session helpers (used in both demo and live mode). We read the
// role the user picked on the login form so demo mode can still show
// "you're signed in as a Teacher".
// ---------------------------------------------------------------------------

function readLocalSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readLocalProfiles() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalProfiles(profiles) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(profiles));
  } catch {
    // Storage may be full or blocked; keep in-memory state.
  }
}

// The demo-mode "team" the Collaboration Hub falls back to. Just enough
// so the page doesn't look empty during a sales demo. Real customers
// running with Supabase creds see actual teammates from user_profiles.
function demoTeamRoster(currentSession) {
  const currentEmail = currentSession?.email || '';
  const currentRole  = currentSession?.role  || 'teacher';
  const currentName  = deriveNameFromEmail(currentEmail) || 'You';

  const stubs = [
    {
      id: 'demo-you',
      email: currentEmail,
      displayName: currentName,
      role: currentRole,
      discipline: '',
    },
    {
      id: 'demo-slp',
      email: 'jamie.rivera@edutracker.edu',
      displayName: 'Jamie Rivera',
      role: 'related_service',
      discipline: 'SLP',
    },
    {
      id: 'demo-ot',
      email: 'aisha.patel@edutracker.edu',
      displayName: 'Aisha Patel',
      role: 'related_service',
      discipline: 'OT',
    },
    {
      id: 'demo-behavior',
      email: 'marcus.lopez@edutracker.edu',
      displayName: 'Marcus Lopez',
      role: 'related_service',
      discipline: 'Behavior Specialist',
    },
    {
      id: 'demo-para',
      email: 'kate.owens@edutracker.edu',
      displayName: 'Kate Owens',
      role: 'paraprofessional',
      discipline: '',
    },
  ];

  return stubs.map((s) => normalizeIncoming({
    id: s.id,
    email: s.email,
    display_name: s.displayName,
    role: s.role,
    discipline: s.discipline,
    created_at: new Date().toISOString(),
    last_updated: new Date().toISOString(),
  }));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function UserProfilesProvider({ children }) {
  const supabaseLive = isSupabaseConfigured() && supabase !== null;
  const [profiles, setProfiles]         = useState([]);
  const [currentAuthId, setCurrentAuthId] = useState(null);
  const [loading, setLoading]           = useState(supabaseLive);
  const [error, setError]               = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // -------------------------------------------------------------------------
  // Live mode: load + subscribe to public.user_profiles.
  // -------------------------------------------------------------------------
  const refresh = useCallback(async () => {
    if (!supabaseLive) {
      const session = readLocalSession();
      const stored  = readLocalProfiles();
      const demo    = stored.length > 0 ? stored : demoTeamRoster(session);
      if (stored.length === 0) writeLocalProfiles(demo);
      if (!mounted.current) return;
      setProfiles(demo);
      setCurrentAuthId(session?.userId || 'demo-you');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [{ data: rows, error: fetchError }, { data: authData }] = await Promise.all([
        supabase.from(TABLE).select('*').order('display_name', { ascending: true }),
        supabase.auth.getUser(),
      ]);
      if (!mounted.current) return;
      if (fetchError) {
        // A missing user_profiles table means the Supabase project hasn't
        // had migration 0009 run yet. That's a setup problem, not a data
        // problem — surface it as such (SchemaSetupBanner listens on this
        // pub/sub and shows a "click for setup SQL" panel), and let the
        // rest of the app fall back to the current-session-derived stub
        // profile so pages that only need "who am I" keep working.
        const classified = classifySupabaseError(fetchError);
        if (classified?.kind === 'missing_table') {
          reportSchemaError(fetchError, 'user_profiles.load');
          const session = readLocalSession();
          const authId = authData?.user?.id || session?.userId || null;
          setCurrentAuthId(authId);
          setProfiles([]);
          setError({
            kind: 'schema_setup',
            message: fetchError.message || 'Database is not set up yet.',
            table: classified.table || 'user_profiles',
          });
          setLoading(false);
          return;
        }
        setError(fetchError.message || 'Failed to load teammates');
        setLoading(false);
        return;
      }
      const authId = authData?.user?.id || null;
      setCurrentAuthId(authId);

      let normalized = (rows || []).map(normalizeIncoming).filter(Boolean);

      // Belt-and-braces: if the trigger didn't fire (older account, or
      // migration applied after signup), upsert a bare row for the current
      // user so they immediately appear in the roster.
      if (authId && !normalized.some((p) => p.id === authId)) {
        const email = authData?.user?.email || '';
        const meta  = authData?.user?.user_metadata || {};
        const displayName =
          meta.full_name || meta.name || meta.display_name || null;
        const { data: inserted } = await supabase
          .from(TABLE)
          .upsert(
            { id: authId, email, display_name: displayName, role: meta.role || null },
            { onConflict: 'id' },
          )
          .select('*')
          .single();
        if (inserted) normalized = [...normalized, normalizeIncoming(inserted)];
      }

      setProfiles(normalized);
      setLoading(false);
    } catch (err) {
      if (!mounted.current) return;
      setError(err?.message || 'Failed to load teammates');
      setLoading(false);
    }
  }, [supabaseLive]);

  useEffect(() => {
    if (!supabaseLive) {
      refresh();
      return undefined;
    }
    refresh();
    const channel = supabase
      .channel('public:user_profiles')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        () => refresh(),
      )
      .subscribe();

    let authSub = null;
    try {
      const { data } = supabase.auth.onAuthStateChange(() => refresh());
      authSub = data?.subscription || null;
    } catch {
      authSub = null;
    }

    return () => {
      supabase.removeChannel(channel);
      try { authSub?.unsubscribe?.(); } catch { /* ignore */ }
    };
  }, [supabaseLive, refresh]);

  // Demo-mode cross-tab sync.
  useEffect(() => {
    if (supabaseLive) return undefined;
    const onStorage = (event) => {
      if (event.key === DEMO_STORAGE_KEY) refresh();
      if (event.key === SESSION_KEY) refresh();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    return undefined;
  }, [supabaseLive, refresh]);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const updateMyProfile = useCallback(
    async (patch) => {
      if (!currentAuthId) throw new Error('Not signed in.');

      if (!supabaseLive) {
        const current = readLocalProfiles();
        const next = current.map((p) =>
          p.id === currentAuthId
            ? { ...p, ...patch, lastUpdated: new Date().toISOString() }
            : p,
        );
        writeLocalProfiles(next);
        setProfiles(next);
        return next.find((p) => p.id === currentAuthId) || null;
      }

      const patchRow = toPatchRow(patch);
      if (Object.keys(patchRow).length === 0) {
        return profiles.find((p) => p.id === currentAuthId) || null;
      }

      const { data, error: updateError } = await supabase
        .from(TABLE)
        .update(patchRow)
        .eq('id', currentAuthId)
        .select('*')
        .single();
      if (updateError) {
        // Same setup-error handling as the read path. If the table
        // doesn't exist we can't save a profile — surface a friendlier
        // error message than the raw Postgres one so ProfileSetupPrompt
        // doesn't just say "Could not find the table 'public.user_profiles'
        // in the schema cache" back to a teacher.
        const classified = classifySupabaseError(updateError);
        if (classified?.kind === 'missing_table') {
          reportSchemaError(updateError, 'user_profiles.update');
          const err = new Error(
            'Your Supabase database is missing the user_profiles table. ' +
            'Run the setup script (see the yellow banner at the top of the app) ' +
            'and try again.',
          );
          err.schemaSetup = true;
          throw err;
        }
        throw updateError;
      }
      const saved = normalizeIncoming(data);
      setProfiles((prev) => prev.map((p) => (p.id === currentAuthId ? saved : p)));
      return saved;
    },
    [supabaseLive, currentAuthId, profiles],
  );

  // -------------------------------------------------------------------------
  // Lookup helpers
  // -------------------------------------------------------------------------

  const byId = useMemo(() => {
    const map = new Map();
    for (const p of profiles) if (p?.id) map.set(p.id, p);
    return map;
  }, [profiles]);

  const getUserById = useCallback(
    (userId) => (userId ? byId.get(userId) || null : null),
    [byId],
  );

  const me = useMemo(
    () => (currentAuthId ? byId.get(currentAuthId) || null : null),
    [byId, currentAuthId],
  );

  // "Set your display name and role" is required if the user is signed in
  // AND either no profile row exists yet, or exists but has no display_name.
  // Suppress this if the Supabase database is missing user_profiles — the
  // teacher can't save a profile against a table that doesn't exist, and
  // the SchemaSetupBanner will already be nagging them to run the setup
  // script.
  const needsProfileSetup = useMemo(() => {
    if (loading) return false;
    if (!currentAuthId) return false;
    if (error?.kind === 'schema_setup') return false;
    if (!me) return true;
    return !me.displayName || !me.role;
  }, [loading, currentAuthId, me, error]);

  const value = useMemo(
    () => ({
      profiles,
      loading,
      error,
      isLive: supabaseLive,
      me,
      currentAuthId,
      needsProfileSetup,
      refresh,
      updateMyProfile,
      getUserById,
    }),
    [profiles, loading, error, supabaseLive, me, currentAuthId, needsProfileSetup, refresh, updateMyProfile, getUserById],
  );

  return (
    <UserProfilesContext.Provider value={value}>
      {children}
    </UserProfilesContext.Provider>
  );
}

export function useUserProfiles() {
  const ctx = useContext(UserProfilesContext);
  if (!ctx) {
    throw new Error('useUserProfiles must be used within a <UserProfilesProvider>');
  }
  return ctx;
}
